import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { setLicenseRedirectHandler } from "./api/client";
import { useAuth } from "./context/AuthContext";
import { useLicense } from "./context/LicenseContext";
import Loader from "./components/Loader";
import PwaUpdateToast from "./components/PwaUpdateToast";
import AppHomePage from "./pages/AppHomePage";
import HomePage from "./pages/HomePage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
// Deliberately eager (not lazy): this is the axios interceptor's redirect target and must render even when API requests are failing.
import NoLicensePage from "./pages/NoLicensePage";

const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ChatsPage = lazy(() => import("./pages/ChatsPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const DeveloperPage = lazy(() => import("./pages/DeveloperPage"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const CveLibraryPage = lazy(() => import("./pages/CveLibraryPage"));
const ProductDocumentationPage = lazy(() => import("./pages/ProductDocumentationPage"));
const AgentsPage = lazy(() => import("./pages/AgentsPage"));
const UatPage = lazy(() => import("./pages/UatPage"));
const TraceAnalyzerPage = lazy(() => import("./pages/TraceAnalyzerPage"));
const TraceAnalyzerProjectPage = lazy(() => import("./pages/TraceAnalyzerProjectPage"));
const TraceAnalyzerJobPage = lazy(() => import("./pages/TraceAnalyzerJobPage"));
const TraceAnalyzerRolePage = lazy(() => import("./pages/TraceAnalyzerRolePage"));
const TraceAnalyzerSettingsPage = lazy(() => import("./pages/TraceAnalyzerSettingsPage"));
const RoleBuilderPage = lazy(() => import("./pages/RoleBuilderPage"));
const RoleBuilderWorkspacePage = lazy(() => import("./pages/RoleBuilderWorkspacePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SapEnvironmentPage = lazy(() => import("./pages/SapEnvironmentPage"));
const GlobalLicenseRulesetPage = lazy(() => import("./pages/GlobalLicenseRulesetPage"));
const OrganizationSettingsPage = lazy(() => import("./pages/OrganizationSettingsPage"));
const AdminOrganizationsPage = lazy(() => import("./pages/AdminOrganizationsPage"));
const AdminCatalogPage = lazy(() => import("./pages/AdminCatalogPage"));
const AdminAuthorizationConceptPage = lazy(() => import("./pages/AdminAuthorizationConceptPage"));
const OrgUnitsPage = lazy(() => import("./pages/OrgUnitsPage"));

function PageLoader() {
  return <Loader />;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, bootstrapping } = useAuth();
  if (bootstrapping) {
    return <Loader />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function PlatformAdminRoute({ children }) {
  const { isAuthenticated, bootstrapping, user } = useAuth();
  if (bootstrapping) {
    return <Loader />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (user?.role !== "admin") {
    return <Navigate to="/home" replace />;
  }
  return children;
}

export function LicensedRoute({ children }) {
  const { isAuthenticated, bootstrapping } = useAuth();
  const { loading, isActive } = useLicense();

  if (bootstrapping) {
    return <Loader />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (loading) {
    return <Loader />;
  }
  if (!isActive) {
    return <Navigate to="/no-license" replace />;
  }
  return children;
}

function NotFoundRedirect() {
  const { isAuthenticated, bootstrapping } = useAuth();
  if (bootstrapping) {
    return <Loader />;
  }
  return <Navigate to={isAuthenticated ? "/home" : "/"} replace />;
}

export default function App() {
  const navigate = useNavigate();

  // Registers an in-app (SPA) navigation for the axios response interceptor
  // in src/api/client.js — a rejected request signaling "no license" (402,
  // or 403 with detail.code === "license_required") calls this instead of
  // forcing a hard reload.
  useEffect(() => {
    setLicenseRedirectHandler((path) => navigate(path, { replace: true }));
    return () => setLicenseRedirectHandler(null);
  }, [navigate]);

  return (
    <Sentry.ErrorBoundary fallback={<p style={{ padding: "2rem" }}>Something went wrong. Please reload the page.</p>}>
      <PwaUpdateToast />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <AppHomePage />
              </ProtectedRoute>
            }
          />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/auth" element={<Navigate to="/" replace />} />
          <Route
            path="/chats"
            element={
              <LicensedRoute>
                <ChatsPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <LicensedRoute>
                <ChatPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/chat/:conversationId"
            element={
              <LicensedRoute>
                <ChatPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/no-license"
            element={
              <ProtectedRoute>
                <NoLicensePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/configurations/sap"
            element={
              <ProtectedRoute>
                <SapEnvironmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/configurations/license-ruleset"
            element={
              <ProtectedRoute>
                <GlobalLicenseRulesetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization"
            element={
              <ProtectedRoute>
                <OrganizationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <PlatformAdminRoute>
                <AdminOrganizationsPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/catalog"
            element={
              <PlatformAdminRoute>
                <AdminCatalogPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/authorization-concept"
            element={
              <PlatformAdminRoute>
                <AdminAuthorizationConceptPage />
              </PlatformAdminRoute>
            }
          />
          <Route path="/config" element={<Navigate to="/projects" replace />} />
          <Route
            path="/projects"
            element={
              <LicensedRoute>
                <ProjectsPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/projects/:id/org-units"
            element={
              <LicensedRoute>
                <OrgUnitsPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <LicensedRoute>
                <ProjectDetailPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/developer"
            element={
              <LicensedRoute>
                <DeveloperPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/developer/:conversationId"
            element={
              <LicensedRoute>
                <DeveloperPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/library"
            element={
              <LicensedRoute>
                <LibraryPage />
              </LicensedRoute>
            }
          />
          <Route path="/knowledge" element={<Navigate to="/library?tab=knowledge" replace />} />
          <Route
            path="/library/cves"
            element={
              <LicensedRoute>
                <CveLibraryPage />
              </LicensedRoute>
            }
          />
          {/* Protected, not licensed: users locked out at /no-license must still
              be able to read the manual. */}
          {["/product-documentation", "/product-documentation/:sectionSlug", "/product-documentation/:sectionSlug/:articleSlug"].map(
            (path) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute>
                    <ProductDocumentationPage />
                  </ProtectedRoute>
                }
              />
            ),
          )}
          <Route
            path="/agents"
            element={
              <LicensedRoute>
                <AgentsPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/agents/uat"
            element={
              <LicensedRoute>
                <UatPage />
              </LicensedRoute>
            }
          />
          <Route path="/uat" element={<Navigate to="/agents/uat" replace />} />
          <Route
            path="/agents/trace-analyzer/projects/:projectId"
            element={
              <LicensedRoute>
                <TraceAnalyzerProjectPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/agents/trace-analyzer"
            element={
              <LicensedRoute>
                <TraceAnalyzerPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/agents/trace-analyzer/settings"
            element={
              <LicensedRoute>
                <TraceAnalyzerSettingsPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/agents/trace-analyzer/jobs/:id"
            element={
              <LicensedRoute>
                <TraceAnalyzerJobPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/agents/trace-analyzer/roles/:id"
            element={
              <LicensedRoute>
                <TraceAnalyzerRolePage />
              </LicensedRoute>
            }
          />
          <Route
            path="/agents/role-builder"
            element={
              <LicensedRoute>
                <RoleBuilderPage />
              </LicensedRoute>
            }
          />
          <Route
            path="/agents/role-builder/roles/:roleId"
            element={
              <LicensedRoute>
                <RoleBuilderWorkspacePage />
              </LicensedRoute>
            }
          />
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}
